// src/ui/theme/buildAntdTheme.js
// Construye el theme de AntD (educaBrandTheme) desde los tokens runtime, para
// que los componentes AntD del frontal profesor (botones, tags, inputs, menus,
// tablas) respondan EN VIVO a los overrides del editor (F1.4).
//
// Sin overrides => valores actuales => identico a educaBrandTheme. AntD deriva
// hover/active a partir de colorPrimary (valor hex real), por eso pasamos el
// color resuelto, no `var(--edu-*)`.
import React, { useMemo } from "react";
import { ConfigProvider } from "antd";
import { educaBrandTheme } from "./tokens";
import { SEMANTIC_TOKENS } from "./semanticTokens";
import { useThemeRuntime } from "./ThemeRuntimeProvider";

function resolved(token, overrides) {
  return overrides?.[token] ?? SEMANTIC_TOKENS[token].value;
}

function toPx(value, fallback) {
  const n = parseInt(value, 10);
  return Number.isFinite(n) ? n : fallback;
}

export function buildAntdTheme(overrides = {}) {
  const primary = resolved("color.primary", overrides);
  const radiusControl = toPx(resolved("radius.control", overrides), 12);
  const radiusCard = toPx(resolved("radius.card", overrides), 16);

  return {
    ...educaBrandTheme,
    token: {
      ...educaBrandTheme.token,
      colorPrimary: primary,
      colorInfo: primary,
      colorSuccess: primary,
      colorError: resolved("color.danger", overrides),
      colorText: resolved("color.text", overrides),
      colorTextSecondary: resolved("color.textMuted", overrides),
      colorBorderSecondary: resolved("color.border", overrides),
      colorBgLayout: resolved("color.canvas", overrides),
      borderRadius: radiusControl,
      fontFamily: resolved("font.body", overrides),
      fontWeightStrong: toPx(resolved("font.displayWeight", overrides), 800),
    },
    components: {
      ...educaBrandTheme.components,
      Card: {
        ...educaBrandTheme.components.Card,
        borderRadiusLG: radiusCard,
      },
      Menu: {
        ...educaBrandTheme.components.Menu,
        darkItemBg: resolved("color.sidebarBg", overrides),
        darkPopupBg: resolved("color.sidebarBg", overrides),
        darkItemColor: resolved("sidebar.itemText", overrides),
        darkItemSelectedBg: resolved("sidebar.itemActiveBg", overrides),
        darkItemSelectedColor: resolved("sidebar.itemActiveText", overrides),
        darkItemHoverBg: resolved("sidebar.itemHoverBg", overrides),
        darkItemHoverColor: resolved("sidebar.itemActiveText", overrides),
        itemBorderRadius: toPx(resolved("sidebar.itemRadius", overrides), 12),
      },
    },
  };
}

// Hook reactivo: el theme se reconstruye con la cascada resuelta (base + pagina
// actual) cuando cambian los overrides o la ruta navegada.
export function useBrandTheme() {
  const { resolvedOverrides } = useThemeRuntime();
  return useMemo(() => buildAntdTheme(resolvedOverrides), [resolvedOverrides]);
}

// Wrapper reactivo: sustituye a <ConfigProvider theme={educaBrandTheme}>. Encapsula
// el hook para migrar las pantallas del profesor sin tocar su logica interna.
export function BrandThemeProvider({ children }) {
  const theme = useBrandTheme();
  return <ConfigProvider theme={theme}>{children}</ConfigProvider>;
}

export default buildAntdTheme;
